import React, { useState, useRef, ChangeEvent, FormEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput, {
  isValidPhoneNumber,
  getCountries,
} from "react-phone-number-input";
import type { CountryCode } from "libphonenumber-js";
import "react-phone-number-input/style.css";
import { useAppSelector } from "../../hooks/redux/useRedux";
import { selectLocationCode } from "../../redux/slices/profileSlice/ProfileSlice";
import {
  TbArrowLeft,
  TbEdit,
  TbCheck,
  TbX,
  TbMapPin,
  TbCalendar,
  TbCurrencyDollar,
  TbUsers,
  TbBuildingStore,
  TbWorld,
  TbBrandInstagram,
  TbBrandFacebook,
  TbBrandLinkedin,
  TbBrandX,
  TbBrandTiktok,
  TbBrandYoutube,
  TbPhoto,
  TbUpload,
  TbChevronDown,
  TbTool,
  TbUser,
  TbPhone,
  TbMail,
} from "react-icons/tb";
import toast from "react-hot-toast";
import { IMarketPlace } from "../../api/marketPlace/marketPlaceModel";
import {
  putMarketPlaceForm,
  deleteMarketPlace,
} from "../../api/marketPlace/marketPlaceService";

// ── Country data ───────────────────────────────────────────────────────────────

const COUNTRY_CODES = [
  "AF",
  "AL",
  "DZ",
  "AD",
  "AO",
  "AG",
  "AR",
  "AM",
  "AU",
  "AT",
  "AZ",
  "BS",
  "BH",
  "BD",
  "BB",
  "BY",
  "BE",
  "BZ",
  "BJ",
  "BT",
  "BO",
  "BA",
  "BW",
  "BR",
  "BN",
  "BG",
  "BF",
  "BI",
  "CV",
  "KH",
  "CM",
  "CA",
  "CF",
  "TD",
  "CL",
  "CN",
  "CO",
  "KM",
  "CG",
  "CD",
  "CR",
  "HR",
  "CU",
  "CY",
  "CZ",
  "DK",
  "DJ",
  "DM",
  "DO",
  "EC",
  "EG",
  "SV",
  "GQ",
  "ER",
  "EE",
  "SZ",
  "ET",
  "FJ",
  "FI",
  "FR",
  "GA",
  "GM",
  "GE",
  "DE",
  "GH",
  "GR",
  "GD",
  "GT",
  "GN",
  "GW",
  "GY",
  "HT",
  "HN",
  "HU",
  "IS",
  "IN",
  "ID",
  "IR",
  "IQ",
  "IE",
  "IL",
  "IT",
  "JM",
  "JP",
  "JO",
  "KZ",
  "KE",
  "KI",
  "KW",
  "KG",
  "LA",
  "LV",
  "LB",
  "LS",
  "LR",
  "LY",
  "LI",
  "LT",
  "LU",
  "MG",
  "MW",
  "MY",
  "MV",
  "ML",
  "MT",
  "MH",
  "MR",
  "MU",
  "MX",
  "FM",
  "MD",
  "MC",
  "MN",
  "ME",
  "MA",
  "MZ",
  "MM",
  "NA",
  "NR",
  "NP",
  "NL",
  "NZ",
  "NI",
  "NE",
  "NG",
  "NO",
  "OM",
  "PK",
  "PW",
  "PA",
  "PG",
  "PY",
  "PE",
  "PH",
  "PL",
  "PT",
  "QA",
  "RO",
  "RU",
  "RW",
  "WS",
  "SM",
  "SA",
  "SN",
  "RS",
  "SC",
  "SL",
  "SG",
  "SK",
  "SI",
  "SB",
  "SO",
  "ZA",
  "SS",
  "ES",
  "LK",
  "SD",
  "SR",
  "SE",
  "CH",
  "SY",
  "TW",
  "TJ",
  "TZ",
  "TH",
  "TL",
  "TG",
  "TO",
  "TT",
  "TN",
  "TR",
  "TM",
  "TV",
  "UG",
  "UA",
  "AE",
  "GB",
  "US",
  "UY",
  "UZ",
  "VU",
  "VE",
  "VN",
  "YE",
  "ZM",
  "ZW",
];
const _dn = new Intl.DisplayNames(["en"], { type: "region" });
const COUNTRIES = COUNTRY_CODES.map((code) => ({
  code,
  name: _dn.of(code) ?? code,
})).sort((a, b) => a.name.localeCompare(b.name));

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmt = (val?: string | null) =>
  val ? `$${Number(val).toLocaleString()}` : "—";

const inputCls =
  "w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all";

const noE = (e: React.KeyboardEvent<HTMLInputElement>) =>
  ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

const noEInt = (e: React.KeyboardEvent<HTMLInputElement>) =>
  [".", ",", "e", "E", "+", "-"].includes(e.key) && e.preventDefault();

// ── CountrySelect ──────────────────────────────────────────────────────────────

function CountrySelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = COUNTRIES.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${inputCls} flex items-center justify-between text-left`}
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value || "Select country"}
        </span>
        <TbChevronDown
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
          >
            <div className="p-2 border-b border-gray-100">
              <input
                autoFocus
                type="text"
                placeholder="Search country..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400"
              />
            </div>
            <div className="max-h-52 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-400 px-4 py-3 text-center">
                  No results
                </p>
              ) : (
                filtered.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => {
                      onChange(c.name);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${value === c.name ? "bg-indigo-50 text-indigo-600 font-medium" : "text-gray-700"}`}
                  >
                    {c.name}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function InfoRow({
  label,
  value,
  icon,
}: {
  label: string;
  value?: string | number | null;
  icon?: React.ReactNode;
}) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-2.5">
      {icon && <span className="mt-0.5 text-gray-400 shrink-0">{icon}</span>}
      <div>
        <p className="text-[10px] text-gray-400 uppercase tracking-wide">
          {label}
        </p>
        <p className="text-sm text-gray-800 font-medium">{value}</p>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  color = "text-gray-900",
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div className="bg-gray-50 rounded-xl px-4 py-3 text-center">
      <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className={`text-base font-bold ${color}`}>{value}</p>
    </div>
  );
}

function SocialLink({
  href,
  icon,
  label,
}: {
  href?: string | null;
  icon: React.ReactNode;
  label: string;
}) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50 transition-colors text-xs text-gray-600 hover:text-indigo-600"
    >
      {icon}
      {label}
    </a>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// ── Edit form state ─────────────────────────────────────────────────────────────

interface EditForm {
  business_name: string;
  description: string;
  logo_image: File | null;
  country: string;
  city: string;
  year_founded: string;
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  asking_price: string;
  average_monthly_revenue: string;
  average_monthly_net_profit: string;
  active_client_count: string;
  reason_for_selling: string;
  monthly_expenses: string;
  repeat_client_percentage: string;
  employee_count: string;
  contractor_count: string;
  website_url: string;
  instagram_url: string;
  facebook_url: string;
  linkedin_url: string;
  x_url: string;
  tiktok_url: string;
  youtube_url: string;
  google_business_profile_url: string;
  physical_lease: string;
  equipment: string;
  inventory: string;
  vehicles: string;
  office_furniture_and_fixtures: string;
  specialty_tools: string;
  business_owner?: boolean | null;
  industry_title?: string | null;
}

const toEditForm = (biz: IMarketPlace): EditForm => ({
  business_name: biz.business_name ?? "",
  description: biz.description ?? "",
  logo_image: null,
  country: biz.country ?? "",
  city: biz.city ?? "",
  year_founded: String(biz.year_founded ?? ""),
  owner_name: biz.owner_name ?? "",
  owner_email: biz.owner_email ?? "",
  owner_phone: biz.owner_phone ?? "",
  asking_price: biz.asking_price ?? "",
  average_monthly_revenue: biz.average_monthly_revenue ?? "",
  average_monthly_net_profit: biz.average_monthly_net_profit ?? "",
  active_client_count: String(biz.active_client_count ?? ""),
  reason_for_selling: biz.reason_for_selling ?? "",
  monthly_expenses: biz.monthly_expenses ?? "",
  repeat_client_percentage: biz.repeat_client_percentage ?? "",
  employee_count: String(biz.employee_count ?? ""),
  contractor_count: String(biz.contractor_count ?? ""),
  website_url: biz.website_url ?? "",
  instagram_url: biz.instagram_url ?? "",
  facebook_url: biz.facebook_url ?? "",
  linkedin_url: biz.linkedin_url ?? "",
  x_url: biz.x_url ?? "",
  tiktok_url: biz.tiktok_url ?? "",
  youtube_url: biz.youtube_url ?? "",
  google_business_profile_url: biz.google_business_profile_url ?? "",
  physical_lease: biz.physical_lease ?? "",
  equipment: biz.equipment ?? "",
  inventory: biz.inventory ?? "",
  vehicles: biz.vehicles ?? "",
  office_furniture_and_fixtures: biz.office_furniture_and_fixtures ?? "",
  specialty_tools: biz.specialty_tools ?? "",
  business_owner: biz.business_owner ?? null,
  industry_title: biz.industry_title ?? null,
});

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  biz: IMarketPlace;
  onBack: () => void;
  onUpdated: (updated: IMarketPlace) => void;
  onDeleted: (id: number) => void;
}

export default function BusinessDetailPage({
  biz,
  onBack,
  onUpdated,
  onDeleted,
}: Props) {
  const locationCode = useAppSelector(selectLocationCode);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState<EditForm>(toEditForm(biz));
  const [logoPreview, setLogoPreview] = useState<string | null>(
    biz.logo_image ?? null,
  );
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const set =
    (field: keyof EditForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleLogo = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({ ...prev, logo_image: file }));
    setLogoPreview(URL.createObjectURL(file));
  };

  const cancelEdit = () => {
    setForm(toEditForm(biz));
    setLogoPreview(biz.logo_image ?? null);
    setPhoneError("");
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!biz.id) return;
    try {
      setDeleting(true);
      await deleteMarketPlace(biz.id);
      toast.success("Listing deleted.");
      onDeleted(biz.id);
    } catch {
      toast.error("Failed to delete. Please try again.");
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.owner_phone || !isValidPhoneNumber(form.owner_phone)) {
      setPhoneError("Please enter a valid phone number.");
      return;
    }
    const fd = new FormData();
    const fields: Array<keyof EditForm> = [
      "business_name",
      "description",
      "country",
      "city",
      "year_founded",
      "owner_name",
      "owner_email",
      "owner_phone",
      "asking_price",
      "average_monthly_revenue",
      "average_monthly_net_profit",
      "active_client_count",
      "reason_for_selling",
      "monthly_expenses",
      "repeat_client_percentage",
      "employee_count",
      "contractor_count",
      "website_url",
      "instagram_url",
      "facebook_url",
      "linkedin_url",
      "x_url",
      "tiktok_url",
      "youtube_url",
      "google_business_profile_url",
      "physical_lease",
      "equipment",
      "inventory",
      "vehicles",
      "office_furniture_and_fixtures",
      "specialty_tools",
    ];
    fields.forEach((key) => {
      const val = form[key];
      if (val && typeof val === "string") fd.append(key, val);
    });
    if (form.logo_image) fd.append("logo_image", form.logo_image);

    try {
      setSaving(true);
      const res = await putMarketPlaceForm(biz.id!, fd);
      toast.success("Business updated successfully!");
      onUpdated(res.data);
      setEditing(false);
    } catch {
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const socials = [
    {
      href: biz.website_url,
      icon: <TbWorld className="w-3.5 h-3.5" />,
      label: "Website",
    },
    {
      href: biz.instagram_url,
      icon: <TbBrandInstagram className="w-3.5 h-3.5" />,
      label: "Instagram",
    },
    {
      href: biz.facebook_url,
      icon: <TbBrandFacebook className="w-3.5 h-3.5" />,
      label: "Facebook",
    },
    {
      href: biz.linkedin_url,
      icon: <TbBrandLinkedin className="w-3.5 h-3.5" />,
      label: "LinkedIn",
    },
    { href: biz.x_url, icon: <TbBrandX className="w-3.5 h-3.5" />, label: "X" },
    {
      href: biz.tiktok_url,
      icon: <TbBrandTiktok className="w-3.5 h-3.5" />,
      label: "TikTok",
    },
    {
      href: biz.youtube_url,
      icon: <TbBrandYoutube className="w-3.5 h-3.5" />,
      label: "YouTube",
    },
  ].filter((s) => s.href);

  const assets = [
    { label: "Lease", value: biz.physical_lease },
    { label: "Equipment", value: biz.equipment },
    { label: "Inventory", value: biz.inventory },
    { label: "Vehicles", value: biz.vehicles },
    { label: "Furniture & Fixtures", value: biz.office_furniture_and_fixtures },
    { label: "Specialty Tools", value: biz.specialty_tools },
  ].filter((a) => a.value);

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="min-h-screen bg-gradient-to-br from-white to-gray-50 pb-16"
    >
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-100 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-600 transition-colors"
          >
            <TbArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 grow">
            <h1 className="text-base font-semibold text-gray-900 truncate">
              {editing ? "Edit Listing" : biz.business_name}
            </h1>
            <p className="text-xs text-gray-400">
              {editing ? "Update your business details" : "Business details"}
            </p>
          </div>
          {biz.business_owner && (
            <>
              {!editing ? (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-red-200 text-red-500 text-sm font-medium hover:bg-red-50 transition-colors"
                  >
                    <TbX className="w-4 h-4" />
                    Delete
                  </button>
                  <button
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors"
                  >
                    <TbEdit className="w-4 h-4" />
                    Edit
                  </button>
                </div>
              ) : (
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    <TbX className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    form="edit-form"
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <TbCheck className="w-4 h-4" />
                    )}
                    Save
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <AnimatePresence mode="wait">
          {/* ────── VIEW MODE ────── */}
          {!editing && (
            <motion.div
              key="view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Hero card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-start gap-4">
                  {biz.logo_image ? (
                    <img
                      src={biz.logo_image}
                      alt={biz.business_name}
                      className="w-20 h-20 rounded-2xl object-cover border border-gray-100 shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-indigo-50 flex items-center justify-center shrink-0">
                      <TbBuildingStore className="w-9 h-9 text-indigo-400" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-900">
                      {biz.business_name}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <TbMapPin className="w-3.5 h-3.5" />
                        {[biz.city, biz.country].filter(Boolean).join(", ")}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <TbCalendar className="w-3.5 h-3.5" />
                        Est. {biz.year_founded}
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                      {biz.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Financial stats */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <TbCurrencyDollar className="w-4 h-4 text-indigo-500" />
                  Financial Overview
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <StatCard
                    label="Asking Price"
                    value={fmt(biz.asking_price)}
                    color="text-gray-900"
                  />
                  <StatCard
                    label="Revenue / mo"
                    value={fmt(biz.average_monthly_revenue)}
                    color="text-green-600"
                  />
                  <StatCard
                    label="Net Profit / mo"
                    value={fmt(biz.average_monthly_net_profit)}
                    color="text-green-700"
                  />
                  <StatCard
                    label="Active Clients"
                    value={String(biz.active_client_count ?? "—")}
                  />
                  {biz.monthly_expenses && (
                    <StatCard
                      label="Expenses / mo"
                      value={fmt(biz.monthly_expenses)}
                      color="text-red-500"
                    />
                  )}
                  {biz.repeat_client_percentage && (
                    <StatCard
                      label="Repeat Clients"
                      value={`${biz.repeat_client_percentage}%`}
                      color="text-indigo-600"
                    />
                  )}
                </div>
                {(biz.employee_count || biz.contractor_count) && (
                  <div className="flex gap-4 mt-4 pt-4 border-t border-gray-50">
                    {biz.employee_count != null && (
                      <InfoRow
                        label="Employees"
                        value={biz.employee_count}
                        icon={<TbUsers className="w-4 h-4" />}
                      />
                    )}
                    {biz.contractor_count != null && (
                      <InfoRow
                        label="Contractors"
                        value={biz.contractor_count}
                        icon={<TbUsers className="w-4 h-4" />}
                      />
                    )}
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-gray-50">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">
                    Reason for Selling
                  </p>
                  <p className="text-sm text-gray-700">
                    {biz.reason_for_selling}
                  </p>
                </div>
              </div>

              {/* Owner */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <TbUser className="w-4 h-4 text-indigo-500" />
                  Owner Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <InfoRow
                    label="Name"
                    value={biz.owner_name}
                    icon={<TbUser className="w-4 h-4" />}
                  />
                  <InfoRow
                    label="Email"
                    value={biz.owner_email}
                    icon={<TbMail className="w-4 h-4" />}
                  />
                  <InfoRow
                    label="Phone"
                    value={biz.owner_phone}
                    icon={<TbPhone className="w-4 h-4" />}
                  />
                </div>
              </div>

              {/* Online presence */}
              {socials.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <TbWorld className="w-4 h-4 text-indigo-500" />
                    Online Presence
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {socials.map((s) => (
                      <SocialLink key={s.label} {...s} />
                    ))}
                  </div>
                </div>
              )}

              {/* Physical assets */}
              {assets.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <TbTool className="w-4 h-4 text-indigo-500" />
                    Physical Assets
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {assets.map((a) => (
                      <StatCard
                        key={a.label}
                        label={a.label}
                        value={fmt(a.value)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ────── EDIT MODE ────── */}
          {editing && (
            <motion.div
              key="edit"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <form id="edit-form" onSubmit={handleSave} className="space-y-6">
                {/* Business Overview */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <TbBuildingStore className="w-4 h-4 text-indigo-500" />
                    Business Overview
                  </h3>

                  {/* Logo */}
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-4 p-4 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-all"
                  >
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="logo"
                        className="w-16 h-16 rounded-xl object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                        <TbPhoto className="w-7 h-7 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {logoPreview ? "Change logo" : "Upload logo"}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        PNG, JPG up to 5 MB
                      </p>
                    </div>
                    <TbUpload className="w-4 h-4 text-gray-400 ml-auto shrink-0" />
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogo}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Field label="Business Name" required>
                        <input
                          className={inputCls}
                          value={form.business_name}
                          onChange={set("business_name")}
                          required
                        />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Description" required>
                        <textarea
                          className={`${inputCls} min-h-[100px] resize-y`}
                          value={form.description}
                          onChange={set("description")}
                          required
                        />
                      </Field>
                    </div>
                    <Field label="Country" required>
                      <CountrySelect
                        value={form.country}
                        onChange={(v) => setForm((p) => ({ ...p, country: v }))}
                      />
                    </Field>
                    <Field label="City" required>
                      <input
                        className={inputCls}
                        value={form.city}
                        onChange={set("city")}
                        required
                      />
                    </Field>
                    <Field label="Year Founded" required>
                      <input
                        type="number"
                        className={inputCls}
                        min={1900}
                        max={new Date().getFullYear()}
                        value={form.year_founded}
                        onChange={set("year_founded")}
                        onKeyDown={noEInt}
                        required
                      />
                    </Field>
                  </div>
                </div>

                {/* Owner */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <TbUser className="w-4 h-4 text-indigo-500" />
                    Owner Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Owner Name" required>
                      <input
                        className={inputCls}
                        value={form.owner_name}
                        onChange={set("owner_name")}
                        required
                      />
                    </Field>
                    <Field label="Owner Email" required>
                      <input
                        type="email"
                        className={inputCls}
                        value={form.owner_email}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            owner_email: e.target.value.toLowerCase(),
                          }))
                        }
                        required
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Owner Phone" required>
                        <div
                          className={`w-full rounded-xl border px-3 py-2.5 transition-all ${phoneError ? "border-red-300 ring-2 ring-red-100" : "border-gray-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100"}`}
                        >
                          <PhoneInput
                            international
                            countryCallingCodeEditable={false}
                            defaultCountry={(() => {
                              const code = String(
                                locationCode?.country_code || "",
                              ).toUpperCase() as CountryCode;
                              return getCountries().includes(code)
                                ? code
                                : ("US" as CountryCode);
                            })()}
                            value={form.owner_phone || undefined}
                            onChange={(value) => {
                              setForm((p) => ({
                                ...p,
                                owner_phone: value ?? "",
                              }));
                              if (value)
                                setPhoneError(
                                  isValidPhoneNumber(value)
                                    ? ""
                                    : "Please enter a valid phone number.",
                                );
                              else setPhoneError("");
                            }}
                            limitMaxLength
                            numberInputProps={{
                              className:
                                "w-full bg-transparent outline-none text-sm text-gray-800 placeholder:text-gray-400",
                            }}
                          />
                        </div>
                        {phoneError && (
                          <p className="text-xs text-red-500 mt-1">
                            {phoneError}
                          </p>
                        )}
                      </Field>
                    </div>
                  </div>
                </div>

                {/* Financials */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <TbCurrencyDollar className="w-4 h-4 text-indigo-500" />
                    Financial Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Asking Price ($)" required>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={inputCls}
                        value={form.asking_price}
                        onChange={set("asking_price")}
                        onKeyDown={noE}
                        required
                      />
                    </Field>
                    <Field label="Avg Monthly Revenue ($)" required>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={inputCls}
                        value={form.average_monthly_revenue}
                        onChange={set("average_monthly_revenue")}
                        onKeyDown={noE}
                        required
                      />
                    </Field>
                    <Field label="Avg Monthly Net Profit ($)" required>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={inputCls}
                        value={form.average_monthly_net_profit}
                        onChange={set("average_monthly_net_profit")}
                        onKeyDown={noE}
                        required
                      />
                    </Field>
                    <Field label="Active Client Count" required>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className={inputCls}
                        value={form.active_client_count}
                        onChange={set("active_client_count")}
                        onKeyDown={noEInt}
                        required
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Reason for Selling" required>
                        <textarea
                          className={`${inputCls} min-h-[80px] resize-y`}
                          value={form.reason_for_selling}
                          onChange={set("reason_for_selling")}
                          required
                        />
                      </Field>
                    </div>
                    <Field label="Monthly Expenses ($)">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={inputCls}
                        value={form.monthly_expenses}
                        onChange={set("monthly_expenses")}
                        onKeyDown={noE}
                      />
                    </Field>
                    <Field label="Repeat Client %">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className={inputCls}
                        value={form.repeat_client_percentage}
                        onChange={set("repeat_client_percentage")}
                        onKeyDown={noE}
                      />
                    </Field>
                     <Field label="Employees">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className={inputCls}
                        value={form.employee_count}
                        onChange={set("employee_count")}
                        onKeyDown={noEInt}
                      />
                    </Field>
                      <Field label="Contractors">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className={inputCls}
                        value={form.contractor_count}
                        onChange={set("contractor_count")}
                        onKeyDown={noEInt}
                      />
                    </Field>
                    <div />
                   
                  
                  </div>
                </div>

                {/* Online Presence */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <TbWorld className="w-4 h-4 text-indigo-500" />
                    Online Presence
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-normal">
                      Optional
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(
                      [
                        ["Website URL", "website_url", "https://example.com"],
                        [
                          "Instagram",
                          "instagram_url",
                          "https://instagram.com/...",
                        ],
                        [
                          "Facebook",
                          "facebook_url",
                          "https://facebook.com/...",
                        ],
                        [
                          "LinkedIn",
                          "linkedin_url",
                          "https://linkedin.com/...",
                        ],
                        ["X (Twitter)", "x_url", "https://x.com/..."],
                        ["TikTok", "tiktok_url", "https://tiktok.com/..."],
                        ["YouTube", "youtube_url", "https://youtube.com/..."],
                      ] as const
                    ).map(([label, field, placeholder]) => (
                      <Field key={field} label={label}>
                        <input
                          type={"url"}
                          className={inputCls}
                          placeholder={placeholder}
                          value={form[field as keyof EditForm] as string}
                          onChange={set(field as keyof EditForm)}
                        />
                      </Field>
                    ))}
                  
                      <Field label="Google Business Profile">
                        <input
                          type="url"
                          className={inputCls}
                          placeholder="https://business.google.com/..."
                          value={form.google_business_profile_url}
                          onChange={set("google_business_profile_url")}
                        />
                      </Field>
               
                  </div>
                </div>

                {/* Physical Assets */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <TbTool className="w-4 h-4 text-indigo-500" />
                    Physical Assets
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-normal">
                      Optional
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(
                      [
                        ["Physical Lease ($)", "physical_lease"],
                        ["Equipment ($)", "equipment"],
                        ["Inventory ($)", "inventory"],
                        ["Vehicles ($)", "vehicles"],
                        [
                          "Office Furniture & Fixtures ($)",
                          "office_furniture_and_fixtures",
                        ],
                        ["Specialty Tools ($)", "specialty_tools"],
                      ] as const
                    ).map(([label, field]) => (
                      <Field key={field} label={label}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={inputCls}
                          placeholder="0.00"
                          value={form[field as keyof EditForm] as string}
                          onChange={set(field as keyof EditForm)}
                          onKeyDown={noE}
                        />
                      </Field>
                    ))}
                  </div>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Delete confirmation modal ── */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm"
            >
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-4 mx-auto">
                <TbX className="w-6 h-6 text-red-500" />
              </div>
              <h2 className="text-base font-semibold text-gray-900 text-center mb-1">
                Delete this listing?
              </h2>
              <p className="text-sm text-gray-400 text-center mb-6">
                <span className="font-medium text-gray-600">
                  {biz.business_name}
                </span>{" "}
                will be permanently removed. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => setConfirmDelete(false)}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={handleDelete}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
